import type { AlertTone, ModuleId } from '@qlk/shared';
import { ALERT_TONES, MODULE_LABEL } from '@qlk/shared';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { EmptyState, Nut, OChon, PageHeader, StatusBadge, ToolbarBang } from '../../../ui/index.js';
import { useCanhBao } from '../api.js';
import { MAU_CHAM } from '../components/KhungCanXuLy.js';

/**
 * `/canh-bao` — chỗ đi tới của liên kết "Xem tất cả cảnh báo →" ở trang Tổng quan.
 * Cùng nguồn dữ liệu `GET /api/v1/dashboard/alerts`, chỉ thêm bộ lọc.
 */

const NHAN_TONE: Record<AlertTone, string> = {
  bad: 'Lỗi',
  warn: 'Cảnh báo',
  info: 'Thông tin',
};

export function TrangCanhBao() {
  const canhBao = useCanhBao();
  const [tone, datTone] = useState<'' | AlertTone>('');
  const [module, datModule] = useState('');

  const items = canhBao.data?.items ?? [];

  const cacModule = useMemo(
    () => [...new Set(items.map((c) => c.module))].sort((a, b) => a.localeCompare(b)),
    [items],
  );

  const loc = items.filter(
    (c) => (tone === '' || c.tone === tone) && (module === '' || c.module === module),
  );

  return (
    <>
      <PageHeader
        breadcrumb={['VẬN HÀNH', 'Tổng quan']}
        title="Cảnh báo cần xử lý"
        phu={`${String(loc.length)} việc`}
        giaiThich="Gộp từ bản quyền, kho nhạc, lịch đăng, kết nối API và đối soát doanh thu."
        actions={
          <Link to="/">
            <Nut kieu="phu">← Về Tổng quan</Nut>
          </Link>
        }
      />

      <ToolbarBang
        trai={
          <>
            <OChon
              label="Mức độ"
              value={tone}
              onChange={(v) => {
                datTone(v as '' | AlertTone);
              }}
              options={[
                { value: '', label: 'Mọi mức độ' },
                ...ALERT_TONES.map((t) => ({ value: t, label: NHAN_TONE[t] })),
              ]}
            />
            <OChon
              label="Module"
              value={module}
              onChange={datModule}
              options={[
                { value: '', label: 'Mọi module' },
                ...cacModule.map((m) => ({ value: m, label: MODULE_LABEL[m] ?? m })),
              ]}
            />
          </>
        }
      />

      {canhBao.data && canhBao.data.unavailable.length > 0 && (
        <p className="mb-4 rounded-the bg-warn-nen px-4 py-3 text-co-ban text-warn-chu">
          Chưa lấy được cảnh báo từ: {canhBao.data.unavailable.join(', ')}
        </p>
      )}

      <section className="rounded-the border border-vien bg-be-mat">
        {canhBao.isPending ? (
          <ul className="flex flex-col gap-4 p-6" aria-hidden="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <li
                key={`khung-${String(i)}`}
                className="h-10 animate-pulse rounded bg-neutral-nen"
              />
            ))}
          </ul>
        ) : canhBao.error ? (
          <EmptyState
            loi
            title="Không tải được danh sách cảnh báo"
            description={canhBao.error.message}
            action={
              <Nut
                kieu="phu"
                onClick={() => {
                  void canhBao.refetch();
                }}
              >
                Thử lại
              </Nut>
            }
          />
        ) : loc.length === 0 ? (
          <EmptyState
            title="Không có cảnh báo nào"
            description={items.length === 0 ? 'Mọi thứ đang ổn.' : 'Thử bỏ bớt bộ lọc để xem thêm.'}
          />
        ) : (
          <ul className="divide-y divide-vien">
            {loc.map((c) => (
              <li key={c.id} className="flex items-start gap-3 px-6 py-4">
                <span
                  className="mt-[7px] inline-block size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: MAU_CHAM[c.tone] }}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <Link to={c.link} className="text-co-ban font-semibold text-chu hover:text-nhan">
                    {c.title}
                  </Link>
                  <p className="text-co-ban text-chu-phu">{c.text}</p>
                </div>
                <StatusBadge tone={c.tone} cham={false}>
                  {MODULE_LABEL[c.module as ModuleId] ?? c.module}
                </StatusBadge>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
